import * as formUtil from '../../util/form-util';
import { IFormMetadataProcessor } from './iFormMetaDataProcessor';
import { FormData, FormFieldDataSet, FormFieldGroup } from '../../forms-ui-form-faas.interface';
import { ProductHierarchy } from '../../product-hierarchy.interface';

export class ProductHierarchyProcessor implements IFormMetadataProcessor {
	async process(formMetadata: FormData): Promise<FormData> {
		// "magic" dataElement names representing our product hierarchy
		const magicOrder = ['capability', 'subCapability', 'product'];

		let formFieldIndex = 0;
		let foundIndex = -1;
		// loop through form fields until we find the 'magic order' or we hit the end without a hit
		while (formFieldIndex < formMetadata.formFields.length - 2 && foundIndex === -1) {
			// get a set of three form fields starting at current index
			const formFields = formMetadata.formFields.slice(formFieldIndex, formFieldIndex + 3);

			// extract data elements for easier processing
			const dataElements = formFields.map((field) => field.dataElement);

			// test if all three formfields are dropdowns and if their 'dataElement' properties match our magic order
			if (formFields.every((elem) => elem.control === 'Dropdown')
				&& JSON.stringify(dataElements) === JSON.stringify(magicOrder)) {
				foundIndex = formFieldIndex;
			} else {
				// no match found. Proceed to next field
				formFieldIndex += 1;
			}
		}

		// foundIndex of -1 implies we didn't find a match by the time we hit the last field. Return.
		if (foundIndex === -1) return formMetadata;

		// make API call to get hierarchy payload
		const hierarchy: ProductHierarchy = await formUtil.loadProductHierarchy();

		const PRODUCT_HIERARCHY = 'productHierarchy';
		// overwrite cascade properties
		Object.assign(formMetadata.formFields[foundIndex], { cascadeGroup: PRODUCT_HIERARCHY, cascadeRank: 0 });
		Object.assign(formMetadata.formFields[foundIndex + 1], { cascadeGroup: PRODUCT_HIERARCHY, cascadeRank: 1 });
		Object.assign(formMetadata.formFields[foundIndex + 2], { cascadeGroup: PRODUCT_HIERARCHY, cascadeRank: 2 });

		// extract capabilities
		const capabilities: FormFieldDataSet[] = hierarchy.capabilities.map(
			(capability) => ({ label: capability.label, value: capability.value }),
		);

		// extract sub-capabilities
		const subCapabilities: FormFieldDataSet[] = hierarchy.capabilities.map(
			(capability) => capability.subcapabilities.map(
				(subCapability) => ({
					label: subCapability.label,
					value: subCapability.value,
					group: capability.value }),
			),
		).flat(1);

		// extract products
		const products: FormFieldDataSet[] = hierarchy.capabilities.map(
			(capability) => capability.subcapabilities.map(
				(subCapability) => subCapability.products.map(
					(product) => ({
						label: product.label,
						value: product.value,
						group: subCapability.value }),
				),
			),
		).flat(2);

		// assign data sets
		formMetadata.formFields[foundIndex].dataSets = capabilities;
		formMetadata.formFields[foundIndex + 1].dataSets = subCapabilities;
		formMetadata.formFields[foundIndex + 2].dataSets = products;

		/**
		 * in our product hierarchy, capabilities' values become subCapability's groups
		 * and subCapabilities' values become product's groups.
		 * In order to reuse the existing data sets for our groups,
		 * we need to massage the data from the { label: string; value: string } to { label: string; name: string; }
		 */
		const subCapabilityGroups: FormFieldGroup[] = capabilities.map(
			(capability) => ({ label: capability.label, name: capability.value }),
		);
		const productGroups: FormFieldGroup[] = subCapabilities.map(
			(subCapability) => ({ label: subCapability.label, name: subCapability.value }),
		);

		// assign groups
		formMetadata.formFields[foundIndex + 1].groups = subCapabilityGroups;
		formMetadata.formFields[foundIndex + 2].groups = productGroups;
		return formMetadata;
	}
}
